/**
 * VYRO · Programs (rituals) — deep links (#rituels, #rituel-N) and per-card tab rotation.
 * Loaded with `defer` from sections/vyro-programs.liquid so it runs on every page load,
 * including when the section is re-rendered by the theme editor.
 */
(() => {
  // Deep links: #rituels (section) and #rituel-N (one ritual). Horizon's client-side navigation
  // does not always honour hash scrolling, so we handle it explicitly.
  const scrollToHash = (hash) => {
    if (!/^#rituel(s|-\d+)$/.test(hash)) return false;
    const el = document.querySelector(hash);
    if (!el) return false;
    // Smooth scrolling gets cancelled by the theme's reveal animations, so jump instantly,
    // leaving room for the sticky header.
    const header = document.querySelector('.header-section, #shopify-section-sections--header, header');
    const offset = header ? header.getBoundingClientRect().height + 16 : 24;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
    // Reveal the target immediately: the scroll-reveal observer can miss an instant jump on page load
    const section = el.closest('.vyro-programs') || el;
    section.querySelectorAll('[data-vyro-reveal], [data-vyro-program]').forEach((n) => n.classList.add('vyro-revealed'));
    return true;
  };
  if (window.location.hash) {
    // Retry after load: lazy media above the section shifts the layout while it loads
    setTimeout(() => scrollToHash(window.location.hash), 150);
    window.addEventListener('load', () => setTimeout(() => scrollToHash(window.location.hash), 400), { once: true });
  }
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href*="#rituel"]');
    if (!a) return;
    const url = new URL(a.href, window.location.href);
    if (url.pathname !== window.location.pathname) return;
    if (scrollToHash(url.hash)) {
      e.preventDefault();
      history.replaceState(null, '', url.hash);
    }
  });
})();

const initPrograms = () => {
  const AUTO_DELAY = 4000; // ms between auto-switches
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('[data-vyro-program]').forEach((card) => {
    if (card.dataset.vyroProgramInit === '1') return;
    card.dataset.vyroProgramInit = '1';

    const panel = card.querySelector('[data-vyro-program-panel]');
    if (!panel) return;
    const tabs = Array.from(panel.querySelectorAll('.vyro-program__tab'));
    const slides = Array.from(panel.querySelectorAll('.vyro-program__slide'));
    if (!tabs.length || !slides.length) return;

    let activeIndex = 0;
    let autoTimer = null;
    let userInteracted = false;
    let inView = false;

    const clearAuto = () => {
      if (autoTimer) {
        clearTimeout(autoTimer);
        autoTimer = null;
      }
    };

    const scheduleAuto = () => {
      clearAuto();
      if (REDUCED || userInteracted || !inView) return;
      autoTimer = setTimeout(() => {
        const next = (activeIndex + 1) % tabs.length;
        setActive(next, { auto: true });
      }, AUTO_DELAY);
    };

    const stopAuto = () => {
      userInteracted = true;
      clearAuto();
      panel.classList.add('is-paused');
      tabs.forEach((t) => t.classList.remove('is-counting'));
    };

    const setActive = (i, opts = {}) => {
      activeIndex = i;
      tabs.forEach((t, idx) => {
        const active = idx === i;
        t.classList.toggle('is-active', active);
        // Reset & retrigger progress animation only when auto-rotating
        t.classList.remove('is-counting');
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) t.removeAttribute('tabindex');
        else t.setAttribute('tabindex', '-1');
      });
      slides.forEach((s, idx) => {
        if (idx === i) {
          s.removeAttribute('hidden');
        } else {
          s.setAttribute('hidden', '');
        }
      });

      if (opts.focus) tabs[i]?.focus({ preventScroll: true });

      if (!userInteracted && !REDUCED && inView) {
        // Force reflow then add is-counting to restart the progress animation
        void tabs[i].offsetWidth;
        tabs[i].classList.add('is-counting');
        scheduleAuto();
      }
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => {
        stopAuto();
        setActive(i);
      });
      tab.addEventListener('keydown', (e) => {
        const handle = (idx) => { stopAuto(); setActive(idx, { focus: true }); };
        if (e.key === 'ArrowRight') { e.preventDefault(); handle((activeIndex + 1) % tabs.length); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); handle((activeIndex - 1 + tabs.length) % tabs.length); }
        if (e.key === 'Home')       { e.preventDefault(); handle(0); }
        if (e.key === 'End')        { e.preventDefault(); handle(tabs.length - 1); }
      });
    });

    // Swipe gestures on slides — counts as user interaction
    let touchStartX = 0;
    let touchStartY = 0;
    const slidesEl = panel.querySelector('[data-vyro-program-slides]');
    slidesEl?.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    slidesEl?.addEventListener('touchend', (e) => {
      if (!touchStartX) return;
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        stopAuto();
        if (dx < 0 && activeIndex < tabs.length - 1) setActive(activeIndex + 1);
        if (dx > 0 && activeIndex > 0)              setActive(activeIndex - 1);
      }
      touchStartX = 0;
    });

    // Pause when card is out of view, resume when it comes back (if user hasn't stopped it)
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting && entry.intersectionRatio > 0.35;
          if (inView) {
            if (!userInteracted && !REDUCED) {
              // Kick the progress bar on the current tab + schedule next
              tabs[activeIndex].classList.remove('is-counting');
              void tabs[activeIndex].offsetWidth;
              tabs[activeIndex].classList.add('is-counting');
              scheduleAuto();
            }
          } else {
            clearAuto();
            tabs.forEach((t) => t.classList.remove('is-counting'));
          }
        });
      },
      { threshold: [0, 0.35, 0.6] }
    );
    io.observe(card);
  });
};
initPrograms();
document.addEventListener('shopify:section:load', initPrograms);



// Sélecteur interactif des programmes : une pastille par rituel, un panneau visible
(() => {
  const init = (root = document) => {
    root.querySelectorAll('[data-vyro-program-selector]').forEach((sel) => {
      if (sel.dataset.vyroSelInit === '1') return;
      sel.dataset.vyroSelInit = '1';
      const chips = sel.querySelectorAll('[data-vyro-chip]');
      const panels = sel.querySelectorAll('[data-vyro-panel]');
      chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          const id = chip.dataset.vyroChip;
          chips.forEach((c) => {
            const on = c === chip;
            c.classList.toggle('is-active', on);
            c.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          panels.forEach((p) => {
            p.classList.toggle('is-active', p.dataset.vyroPanel === id);
          });
        });
      });
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
